import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import auth from "../../fire";
import { useAuthState } from "react-firebase-hooks/auth";
import axiosClient from "../../axiosClient";
import ExerciseProgressionCard from "../../components/ExerciseProgressionCard/ExerciseProgressionCard";
import PageHeader from "../../components/PageHeader/PageHeader";
import ProgressionGraph from "../../components/ProgressionGraphs/ProgressionGraph";
import BasicSelect from "../../components/BasicSelect/BasicSelect";
import TextSkeleton from "../../components/SkeletonComponents/TextSkeleton";

interface GraphTypeOption {
  label: string;
  value: string;
}

const ExerciseProgression = () => {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const exerciseName = searchParams.get("name");

  const [initialLoading, setIsInitialLoading] = useState(true);
  const [allSessionsOfExercise, setAllSessionsOfExercise] = useState<
    null | ExerciseWithParentWorkout[]
  >(null);
  const [selectedWorkout, setSelectedWorkout] = useState<null | String>(null);
  const [selectedGraphType, setSelectedGraphType] = useState("avg-weight");

  const getAllSessionsOfExercise = useCallback(async () => {
    try {
      const fetchPreviousSessionsResponse = await axiosClient.get(
        `/api/exercises/previousSessionsOfExercise`,
        {
          params: {
            exerciseName: exerciseName,
            includeWorkouts: true,
          },
        }
      );
      const previousSessions = fetchPreviousSessionsResponse.data;
      setAllSessionsOfExercise(previousSessions);
      setIsInitialLoading(false);
    } catch (e) {
      console.error("Error fetching sessions of exercise: " + e);
    }
  }, [exerciseName]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    getAllSessionsOfExercise();
  }, [user, loading, getAllSessionsOfExercise, navigate]);

  const handleChartItemClicked = (workoutId: String) => {
    setSelectedWorkout(workoutId);
    const progressionCardElement = document.getElementById(
      `progression-card-${workoutId}`
    );
    if (progressionCardElement) {
      progressionCardElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const handleProgressionCardClicked = (workoutId: String) => {
    setSelectedWorkout(workoutId);
    const progressionGraphCircle = document.getElementById(
      `progression-graph-circle-${workoutId}`
    );

    if (progressionGraphCircle) {
      progressionGraphCircle.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }
  };

  const handleGraphTypeSelected = (graphType: GraphTypeOption) => {
    setSelectedGraphType(graphType.value);
  };

  const amountOfExerciseProgressionCardSkeletons = useMemo(() => {
    const { innerHeight: screenHeight } = window;
    const workoutTimelineContainerHeight = screenHeight - 435;
    return Math.ceil(workoutTimelineContainerHeight / 136);
  }, []);

  return (
    <>
      <PageHeader backLink={`/progress`} pageTitle="Exercise Progress" />
      <div className="m-2">
        <h2 className="font-bold text-gray-900 text-xl tracking-tight">
          {exerciseName}
        </h2>
        <BasicSelect
          id="progression-graph-type"
          className="my-2"
          initialSelectedOption="avg-weight"
          options={[
            { label: "Average Weight", value: "avg-weight" },
            { label: "Average Reps", value: "avg-reps" },
            { label: "Total Volume", value: "total-volume" },
            { label: "Average Volume", value: "avg-volume" },
          ]}
          onSelect={handleGraphTypeSelected}
        />
        {initialLoading ? (
          <TextSkeleton width="100%" height="183px" className="mb-1" />
        ) : (
          <ProgressionGraph
            exerciseSessions={allSessionsOfExercise}
            onChartItemClicked={handleChartItemClicked}
            selectedGraphType={selectedGraphType}
            selectedWorkout={selectedWorkout}
          />
        )}
        <h3 className="text-base font-medium text-gray-900 mt-1 mb-2">
          Workout Timeline
        </h3>
        <div className="flex flex-col gap-y-6 overflow-y-scroll h-[calc(100vh-435px)]">
          {initialLoading ? (
            <>
              {Array.from(
                { length: amountOfExerciseProgressionCardSkeletons },
                (_, index) => (
                  <TextSkeleton key={index} height="112px" className="w-full" />
                )
              )}
            </>
          ) : (
            allSessionsOfExercise &&
            allSessionsOfExercise.map((exerciseSession, index) => {
              return (
                <ExerciseProgressionCard
                  key={exerciseSession._id}
                  exerciseSession={exerciseSession}
                  lastItem={index === allSessionsOfExercise.length - 1}
                  selectedWorkout={selectedWorkout}
                  selectedGraphType={selectedGraphType}
                  onClick={handleProgressionCardClicked}
                />
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default ExerciseProgression;
